// familyTreeAdapter.js - Adaptateur entre le cache GEDCOM compressé et l'API FamilyTree2
//
// Remplace l'ancien familyTreeDataStore (store MobX) : le cache compressé de
// gedcomDataStore est la seule source de vérité, ce module n'a AUCUN état
// propre — uniquement une transformation mémoïsée vers le format FamilyMember
// attendu par la bibliothèque Balkan, des sélecteurs de branche et un BFS de
// parenté (utilisé par la recherche de relations de l'offcanvas).

import gedcomDataStore from '../../gedcom/stores/gedcomDataStore.js';
import familyTownsStore from '../../tabs/familyMap/stores/familyTownsStore.js';
import { getName, getSurname, getGender } from '../../gedcom/formatters/individualFormatters.js';
import { BRANCH_TYPES } from '../../common/utils/branchUtils.js';
import { storeEvents, EVENTS } from '../../common/stores/storeEvents.js';
import { logger } from '../../utils/debug/logger.js';

// --- Mémoïsation -----------------------------------------------------------
// Une seule transformation complète par version du cache. La version est
// approximée par (référence du Map, taille) — setIndividualsCache remplace le
// Map, addIndividual change la taille. CACHE.BUILT et invalidate() couvrent
// les mises à jour en place (ré-extraction AI, reset).

let memo = null;        // { members: FamilyMember[], map: Map<id, FamilyMember> }
let memoSource = null;  // référence du Map individualsCache au moment du build
let memoSize = 0;

storeEvents.subscribe(EVENTS.CACHE.BUILT, () => invalidate());

export function invalidate() {
  memo = null;
  memoSource = null;
  memoSize = 0;
}

/** Date compressée YYYYMMDD → DD/MM/YYYY (même rendu qu'avant refactoring) */
function formatCompressedDate(d) {
  if (!d) return '';
  const s = d.toString();
  if (s.length !== 8) return '';
  return `${s.substring(6, 8)}/${s.substring(4, 6)}/${s.substring(0, 4)}`;
}

/** Construit l'ensemble des FamilyMember depuis le cache compressé (1 passe) */
function buildMembers(cache) {
  // Carte des enfants : déduite des refs père/mère de chaque individu
  const childMap = new Map();
  for (const id of cache.keys()) childMap.set(id, []);
  for (const [id, ind] of cache) {
    if (ind.f && childMap.has(ind.f)) childMap.get(ind.f).push(id);
    if (ind.m && childMap.has(ind.m)) childMap.get(ind.m).push(id);
  }

  const updatedAt = new Date().toISOString();
  const members = [];
  const map = new Map();

  for (const [id, ind] of cache) {
    // Premier prénom + nom de famille
    const fullName = getName(ind);
    const firstName = fullName ? fullName.split(' ')[0] : '';
    const displayName = `${firstName} ${getSurname(ind) || ''}`.trim();

    // Naissance / décès : extraction directe des codes individuels fb/fd
    // (règle projet : les checks hardcodés sur les codes INDIVIDUELS sont valides)
    let birthInfo = '';
    let deathInfo = '';
    let birthDate = '';
    if (Array.isArray(ind.e)) {
      const birthEvent = ind.e.find(evt => evt.t === 'fb');
      if (birthEvent) {
        birthDate = formatCompressedDate(birthEvent.d);
        const town = birthEvent.l ? (familyTownsStore.getTownsData(birthEvent.l)?.town || '') : '';
        birthInfo = birthDate && town ? `${birthDate} (${town})` : birthDate;
      }
      const deathEvent = ind.e.find(evt => evt.t === 'fd');
      if (deathEvent) {
        const deathDate = formatCompressedDate(deathEvent.d);
        const town = deathEvent.l ? (familyTownsStore.getTownsData(deathEvent.l)?.town || '') : '';
        deathInfo = deathDate && town ? `${deathDate} (${town})` : deathDate;
      }
    }

    const member = {
      id,
      sexOrGender: getGender(ind) || 'unknown',
      name: displayName,
      birthInfo,
      deathInfo,
      title: null,
      dateOfBirth: birthDate,
      dateOfDeath: null,
      photo: null,
      fatherId: ind.f || null,
      motherId: ind.m || null,
      stepParentIds: [],
      siblingIds: ind.b || [],
      spouseIds: ind.s || [],
      unmarriedPartnerIds: [],
      childIds: childMap.get(id) || [],
      updatedAt
    };
    members.push(member);
    map.set(id, member);
  }

  return { members, map };
}

function ensureMembers() {
  const cache = gedcomDataStore.individualsCache;
  if (!memo || memoSource !== cache || memoSize !== cache.size) {
    memo = buildMembers(cache);
    memoSource = cache;
    memoSize = cache.size;
    logger.tree.debug(`🔄 FamilyTreeAdapter: ${memo.members.length} membres transformés`);
  }
  return memo;
}

// --- Sélecteurs --------------------------------------------------------------

/** Tous les membres au format FamilyTree2 */
export function getFamilyMembers() {
  return ensureMembers().members;
}

/** Nombre d'individus disponibles (sans déclencher la transformation) */
export function getMemberCount() {
  return gedcomDataStore.individualsCache.size;
}

/** FamilyMember[] pour les IDs demandés (IDs inconnus ignorés) */
export function getMembersByIds(ids) {
  const { map } = ensureMembers();
  return ids.map(i => map.get(i)).filter(Boolean);
}

/**
 * Famille immédiate : la personne, ses parents, conjoints, enfants, frères/sœurs
 */
export function getImmediateFamily(id) {
  const { map } = ensureMembers();
  const root = map.get(id);
  if (!root) return [];

  const ids = new Set([id]);
  if (root.fatherId) ids.add(root.fatherId);
  if (root.motherId) ids.add(root.motherId);
  root.spouseIds.forEach(s => ids.add(s));
  root.childIds.forEach(c => ids.add(c));
  root.siblingIds.forEach(s => ids.add(s));

  return Array.from(ids).map(i => map.get(i)).filter(Boolean);
}

/**
 * Famille étendue avec info de branche (paternelle/maternelle/descendants...).
 * Toujours inclus : la racine, ses parents, ses conjoints et leurs parents.
 * @param {string} rootId
 * @param {number} ancestorGenerations - générations AU-DELÀ des parents (0..n)
 * @param {number} descendantGenerations - générations de descendants (0..n)
 * @returns {Array} FamilyMember[] enrichis d'une propriété `branch`
 */
export function getExtendedFamily(rootId, ancestorGenerations = 0, descendantGenerations = 0) {
  const { map } = ensureMembers();
  const root = map.get(rootId);
  if (!root) return [];

  const branchMap = new Map();
  branchMap.set(rootId, BRANCH_TYPES.ROOT);

  // Parents (toujours) : père → branche paternelle, mère → branche maternelle
  let frontier = [];
  if (root.fatherId) {
    branchMap.set(root.fatherId, BRANCH_TYPES.PATERNAL);
    frontier.push({ id: root.fatherId, branch: BRANCH_TYPES.PATERNAL });
  }
  if (root.motherId) {
    branchMap.set(root.motherId, BRANCH_TYPES.MATERNAL);
    frontier.push({ id: root.motherId, branch: BRANCH_TYPES.MATERNAL });
  }

  // Conjoints (toujours)
  root.spouseIds.forEach(s => branchMap.set(s, BRANCH_TYPES.SPOUSE));

  // Ascendants : chaque génération propage la branche de son point d'entrée
  for (let g = 0; g < ancestorGenerations; g++) {
    const next = [];
    for (const { id, branch } of frontier) {
      const person = map.get(id);
      if (!person) continue;
      for (const parentId of [person.fatherId, person.motherId]) {
        if (parentId) {
          branchMap.set(parentId, branch);
          next.push({ id: parentId, branch });
        }
      }
    }
    frontier = next;
  }

  // Parents des conjoints (toujours)
  root.spouseIds.forEach(spouseId => {
    const spouse = map.get(spouseId);
    if (spouse) {
      if (spouse.motherId) branchMap.set(spouse.motherId, BRANCH_TYPES.SPOUSE_FAMILY);
      if (spouse.fatherId) branchMap.set(spouse.fatherId, BRANCH_TYPES.SPOUSE_FAMILY);
    }
  });

  // Descendants : enfants + leurs conjoints, génération par génération
  if (descendantGenerations >= 1) {
    let descFrontier = root.childIds.slice();
    for (let g = 0; g < descendantGenerations; g++) {
      const next = [];
      for (const childId of descFrontier) {
        const child = map.get(childId);
        if (!child) continue;
        branchMap.set(childId, BRANCH_TYPES.DESCENDANT);
        child.spouseIds.forEach(s => branchMap.set(s, BRANCH_TYPES.SPOUSE));
        next.push(...child.childIds);
      }
      descFrontier = next;
    }
  }

  return Array.from(branchMap.entries())
    .map(([id, branch]) => {
      const member = map.get(id);
      return member ? { ...member, branch } : null;
    })
    .filter(Boolean);
}

// --- Recherche de relations (BFS sur parents/enfants/conjoints) -------------

function neighborsOf(member) {
  const result = [];
  if (member.fatherId) result.push([member.fatherId, 'father']);
  if (member.motherId) result.push([member.motherId, 'mother']);
  member.childIds.forEach(c => result.push([c, 'child']));
  member.spouseIds.forEach(s => result.push([s, 'spouse']));
  return result;
}

/**
 * Chemin le plus court entre deux personnes (toutes relations confondues).
 * @returns {{path: string[]|null, relationships: Array<{from, to, relation}>}}
 */
export function findRelationshipPath(id1, id2) {
  const { map } = ensureMembers();
  if (!id1 || !id2 || !map.has(id1) || !map.has(id2)) {
    return { path: null, relationships: [] };
  }
  if (id1 === id2) return { path: [id1], relationships: [] };

  // BFS avec pointeurs de parcours (pas de copie de chemins partiels)
  const cameFrom = new Map([[id1, null]]); // id → { prev, relation }
  let queue = [id1];

  while (queue.length > 0) {
    const next = [];
    for (const currentId of queue) {
      for (const [neighborId, relation] of neighborsOf(map.get(currentId))) {
        if (cameFrom.has(neighborId) || !map.has(neighborId)) continue;
        cameFrom.set(neighborId, { prev: currentId, relation });
        if (neighborId === id2) {
          // Reconstruction du chemin
          const path = [id2];
          const relationships = [];
          let node = id2;
          while (cameFrom.get(node)) {
            const { prev, relation: rel } = cameFrom.get(node);
            relationships.unshift({ from: prev, to: node, relation: rel });
            path.unshift(prev);
            node = prev;
          }
          return { path, relationships };
        }
        next.push(neighborId);
      }
    }
    queue = next;
  }

  return { path: null, relationships: [] };
}

/** Tous les ancêtres d'une personne avec leur profondeur et leur lien de remontée */
function ancestorsOf(id, map) {
  const ancestors = new Map(); // ancestorId → { depth, via } (via = enfant par lequel on est remonté)
  let frontier = [id];
  let depth = 0;
  while (frontier.length > 0) {
    depth++;
    const next = [];
    for (const currentId of frontier) {
      const person = map.get(currentId);
      if (!person) continue;
      for (const parentId of [person.fatherId, person.motherId]) {
        if (parentId && !ancestors.has(parentId)) {
          ancestors.set(parentId, { depth, via: currentId });
          next.push(parentId);
        }
      }
    }
    frontier = next;
  }
  return ancestors;
}

/**
 * Ancêtres communs de deux personnes.
 * @returns {{ancestors: string[], path: string[]|null}} - tous les ancêtres
 *   communs, et le chemin id1 → ancêtre le plus proche → id2
 */
export function findCommonAncestors(id1, id2) {
  const { map } = ensureMembers();
  if (!id1 || !id2 || !map.has(id1) || !map.has(id2)) {
    return { ancestors: [], path: null };
  }

  const anc1 = ancestorsOf(id1, map);
  const anc2 = ancestorsOf(id2, map);

  // Chaque personne est son propre "ancêtre" à profondeur 0 : couvre le cas
  // où l'une est directement l'ancêtre de l'autre (père/fils, grand-mère/petite-fille)
  anc1.set(id1, { depth: 0, via: null });
  anc2.set(id2, { depth: 0, via: null });

  const common = [...anc1.keys()].filter(a => anc2.has(a));
  if (common.length === 0) return { ancestors: [], path: null };

  // Ancêtre le plus proche = profondeur cumulée minimale
  let closest = common[0];
  let best = anc1.get(closest).depth + anc2.get(closest).depth;
  for (const a of common) {
    const d = anc1.get(a).depth + anc2.get(a).depth;
    if (d < best) { best = d; closest = a; }
  }

  // Chemin id1 → ancêtre (en remontant les `via`), puis ancêtre → id2 (descente)
  const up = [];
  for (let node = closest; node !== id1; node = anc1.get(node).via) up.unshift(node);
  const down = [];
  for (let node = closest; node !== id2; node = anc2.get(node).via) down.push(anc2.get(node).via);

  return { ancestors: common, path: [id1, ...up, ...down] };
}
